import { action, makeObservable, observable, runInAction } from 'mobx';
import { Injectable } from './service.base';
import { AxiosResponse } from 'axios';
import { api } from '~/tools/api/api';
import { EApiUrl } from '~/tools/api/enums';
import { IRedeemAmount, IRedeemLocation } from './service.types';
import { ModelUser } from '~/models/model.user';
import { extractServerError } from '~/tools/error';

@Injectable()
export class RedeemDialogService {
    @observable public open = false;
    @observable public showStep2 = false;
    @observable public showStep1 = true;

    @observable public slideArrowLeftEnabled = false;
    @observable public slideArrowRightEnabled = true;
    @observable public showLoading = false;

    @observable public selectedLocation = '0';
    @observable public redeemAmounts: Array<IRedeemAmount> = [];
    @observable public redeemLocations: Array<IRedeemLocation> = [];
    @observable public lastError: { description: string; code?: string } = { description: '', code: undefined };

    @observable public selectedRedeem: any | null;
    private currentUser: ModelUser | null;
    private authService: any;

    constructor() {
        makeObservable(this);
        const storage: any = window.sessionStorage.getItem('redeemDialog');
        if (storage) {
            this.openFromStorage(storage);
        }
    }

    @action
    handleOpen = async (authService: any) => {
        this.currentUser = authService.currentUser;
        this.authService = authService;

        this.setSelectedRedeem(null);
        this.setSelectedLocation('0');
        await this.getRedeemOptions(this.authService);
        this.addToSessionStorage({ currentUser: this.currentUser, authService });
        this.setOpen(true);
    };

    @action
    setOpen = (value: boolean) => {
        this.open = value;
    };

    @action
    handleClose = () => {
        this.open = false;
        this.showStep1 = true;
        this.showStep2 = false;
        this.setSelectedRedeem(null);
        this.setSelectedLocation('0');
        this.removeFromSessionStorage();
    };

    @action
    handleOpenStep2 = async (alertDialogSrv: any) => {
        if (this.currentUser) {
            try {
                this.setShowLoading(true);
                this.clearLastError();

                const selectedRedeemValue = this.selectedRedeem ? this.selectedRedeem.value : 0;
                const redeemData = {
                    amount: selectedRedeemValue,
                    locationCode: this.selectedLocation,
                };
                const response: AxiosResponse = await api.post(EApiUrl.redeem, redeemData, {
                    headers: {
                        Authorization: this.currentUser.token,
                    },
                });

                this.setShowLoading(false);
                if (response.status === 200) {
                    const currentFreePlay =
                        parseFloat(this.currentUser.freePlay as unknown as string) - parseFloat(selectedRedeemValue);
                    this.currentUser.freePlay = currentFreePlay.toFixed(2) as unknown as number;
                    this.setShowStep1(false);
                    this.setShowStep2(true);
                    this.getRedeemOptions(this.authService);
                    this.setSelectedLocation('0');
                }
            } catch (error: any) {
                this.setShowLoading(false);
                if (error.response && error.response.status && error.response.status === 403) {
                    this.authService.setLastError(extractServerError(error?.response?.data ?? error));
                    this.authService.setShowForbiddenPopup(true);
                } else {
                    this.setLastError(extractServerError(error?.response?.data ?? error));
                    alertDialogSrv.handleOpen();
                }
            }
        }
    };

    @action
    handleOpenStep1 = () => {
        this.showStep1 = true;
        this.showStep2 = false;
    };

    @action
    setShowLoading = (value: boolean) => {
        this.showLoading = value;
    };

    @action
    setShowStep1 = (value: boolean) => {
        this.showStep1 = value;
    };

    @action
    setShowStep2 = (value: boolean) => {
        this.showStep2 = value;
    };

    @action
    handleLocationChange = (event: { target: { value: string } }) => {
        this.setSelectedLocation(event.target.value);
        this.addToSessionStorage({
            currentUser: this.currentUser,
            selectedRedeem: this.selectedRedeem,
            selectedLocation: this.selectedLocation,
        });
    };

    @action
    setRedeemAmounts = (redeemAmounts: Array<IRedeemAmount>) => {
        this.redeemAmounts = redeemAmounts;
    };

    @action
    setRedeemLocations = (redeemLocations: Array<IRedeemLocation>) => {
        this.redeemLocations = redeemLocations;
    };

    @action
    handleSlideChange = (swiper: any) => {
        if (swiper.isBeginning) {
            this.slideArrowLeftEnabled = false;
        } else {
            this.slideArrowLeftEnabled = true;
        }

        if (swiper.isEnd) {
            this.slideArrowRightEnabled = false;
        } else {
            this.slideArrowRightEnabled = true;
        }
    };

    @action
    handleSlideClick = (redeemAmount: IRedeemAmount) => {
        if (!redeemAmount.disabled) {
            const currentActive: IRedeemAmount | undefined = this.redeemAmounts.find(item => item.active === true);
            if (currentActive) {
                currentActive.active = false;
            }

            this.setSelectedRedeem(redeemAmount);
            redeemAmount.active = true;
            this.addToSessionStorage({ currentUser: this.currentUser, selectedRedeem: this.selectedRedeem });
        }
    };

    @action
    getSelectedLocation = (): string => {
        const selectedLocation = this.redeemLocations.find(item => item.code === this.selectedLocation);

        return selectedLocation ? selectedLocation.name : '';
    };

    @action
    onRedeemKeyUp = (e: any, alertDialogSrv: any) => {
        if (this.open && e.keyCode === 13 && this.selectedRedeem && this.selectedLocation !== '0') {
            this.handleOpenStep2(alertDialogSrv);
        }
    };

    @action
    setSelectedRedeem = (redeemAmount: IRedeemAmount | null) => {
        this.selectedRedeem = redeemAmount;
    };

    @action
    setSelectedLocation = (selectedLocation: any) => {
        this.selectedLocation = selectedLocation;
    };

    addToSessionStorage = (data: any) => {
        window.sessionStorage.setItem('redeemDialog', JSON.stringify(data));
    };

    @action
    openFromStorage = async (storage: string) => {
        const storageData = JSON.parse(storage);

        if (storageData?.currentUser) {
            this.open = true;
            this.currentUser = storageData.currentUser;
            await this.getRedeemOptions(storageData.authService);

            if (storageData?.selectedRedeem) {
                setTimeout(() => {
                    this.setSelectedRedeem(storageData.selectedRedeem);
                    const redeem: IRedeemAmount | undefined = this.redeemAmounts.find(
                        item => item.value === this.selectedRedeem.value,
                    );
                    if (redeem) {
                        redeem.active = true;
                    }
                }, 2000);
            }

            if (storageData?.selectedLocation) {
                this.setSelectedLocation(storageData?.selectedLocation);
            }
        }
    };

    removeFromSessionStorage = () => {
        window.sessionStorage.removeItem('redeemDialog');
    };

    @action
    getRedeemOptions = async (authService: any) => {
        if (authService && authService.currentUser) {
            try {
                const response: AxiosResponse = await api.get(EApiUrl.redeemOptions, {
                    headers: {
                        Authorization: authService.currentUser.token,
                    },
                });
                if (response.status === 200) {
                    const userTotalBalance: number = +authService.currentUser.freePlay;
                    const redeemAmounts: Array<IRedeemAmount> = [];

                    for (const item of response.data?.amounts) {
                        redeemAmounts.push({
                            value: +item,
                            disabled: !response.data?.selectable.includes(item),
                            active: false,
                        });
                    }

                    this.setRedeemAmounts(redeemAmounts);

                    const locations: Array<IRedeemLocation> = [];
                    for (const item of response.data?.locations) {
                        locations.push({
                            code: item.code,
                            name: item.display_name,
                        });
                    }
                    this.setRedeemLocations(locations);
                }
            } catch (error: any) {
                if (error.response && error.response.status && error.response.status === 403) {
                    this.authService.setLastError(extractServerError(error?.response?.data ?? error));
                    authService.setShowForbiddenPopup(true);
                } else {
                    this.setLastError(extractServerError(error?.response?.data ?? error));
                }
            }
        }
    };

    @action
    setLastError = (err: { description: string; code?: string }) => {
        this.lastError = err;
    };

    @action
    clearLastError = () => {
        this.lastError = { description: '', code: undefined };
    };
}
